import { useEffect, useState } from 'react';
import { UserSessionHelper } from '@/library';
import FormNavigationLink from '@/common/FormNavigationLink';
import OrderInfoTooltip from './OrderInfoTooltip';
import InformationIcon from '@/assets/icons/information.svg?react';
import CopyIcon from '@/assets/icons/copy.svg?react';
import { OrderSearchResult } from '@/api/models';
import { OrderSort } from './OrderSorting';
import SortableTableHeader from './SortableTableHeader';
import styles from './OrdersTable.module.scss';
import { ordersPresentation, OrdersPageType } from './OrdersPresentation';

const configuredHighlightDuration = Number(import.meta.env.TOURPWA_ORDERS_HIGHLIGHT_DURATION_MS);
const highlightDuration = Number.isFinite(configuredHighlightDuration) && configuredHighlightDuration > 0 ? configuredHighlightDuration : 120_000;

const EMPTY_CELL = '—';
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: 'numeric',
  year: '2-digit',
});

const displayText = (value: string) => value.trim() || EMPTY_CELL;

const formatDate = (value: string | null) => {
  if (!value) return EMPTY_CELL;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_CELL;

  return dateFormatter.format(date);
};

const formatOrderStatus = (status: string) => status.replace('Order', '').trim();

interface IProps {
  currentPage?: OrdersPageType;
  orders: OrderSearchResult[];
  highlightedIds?: number[];
  sortBy: OrderSort;
  statusMessage?: string;
  // eslint-disable-next-line no-unused-vars
  onSortChange: (sortBy: OrderSort) => void;
}

export default function OrdersTable({ currentPage = OrdersPageType.Clubs, orders, highlightedIds = [], sortBy, statusMessage, onSortChange }: IProps) {
  const [highlightExpired, setHighlightExpired] = useState(false);
  const highlightClassName = highlightExpired ? styles.fadingOrder : styles.newOrder;
  const [token, setToken] = useState('');
  const [activeSummary, setActiveSummary] = useState<{ order: OrderSearchResult; anchor: HTMLButtonElement } | null>(null);
  const summaryIsVisible = activeSummary && orders.includes(activeSummary.order);
  const presentation = ordersPresentation[currentPage];
  const columnCount = 7 + Number(presentation.showClubs) + Number(presentation.showPlayer) + Number(presentation.showBuildLocation) + 2 * Number(presentation.showShippingLocations);
  const clubsAlignment = currentPage === OrdersPageType.Components ? 'left' : 'center';
  const isPutters = currentPage === OrdersPageType.Putters;
  const tableClass = `${styles.table} ${currentPage === OrdersPageType.Components ? styles.componentsTable : ''} ${isPutters ? styles.puttersTable : ''}`;

  useEffect(() => {
    const loadToken = async () => {
      const token = await UserSessionHelper.getToken();
      setToken(token || '');
    };
    loadToken();
  }, []);

  useEffect(() => {
    setActiveSummary(null);
  }, [orders, currentPage]);

  useEffect(() => {
    setHighlightExpired(false);
    if (!highlightedIds.length) return;
    const timer = window.setTimeout(() => setHighlightExpired(true), highlightDuration);
    return () => window.clearTimeout(timer);
  }, [highlightedIds]);

  return (
    <div>
      <div className={styles.tableScrollContainer}>
        <table className={tableClass}>
          <thead>
            <tr>
              {presentation.showPlayer && <SortableTableHeader label="Player Name" column="playerName" sortBy={sortBy} onSortChange={onSortChange} />}
              {presentation.showClubs && <SortableTableHeader label="Clubs" column="clubDescriptions" sortBy={sortBy} onSortChange={onSortChange} align={clubsAlignment} />}
              <SortableTableHeader label="Order #" column="orderNo" sortBy={sortBy} onSortChange={onSortChange} />
              <SortableTableHeader label="Created" column="orderDate" sortBy={sortBy} onSortChange={onSortChange} align="center" />
              <SortableTableHeader label="Status" column="orderStatusName" sortBy={sortBy} onSortChange={onSortChange} />
              <SortableTableHeader label="Tour" column="orderAccountName" sortBy={sortBy} onSortChange={onSortChange} />
              {presentation.showBuildLocation && <SortableTableHeader label="Build Loc." column="buildLocationName" sortBy={sortBy} onSortChange={onSortChange} />}
              {presentation.showShippingLocations && (
                <>
                  <SortableTableHeader label="Ship From" column="inventoryLocationName" sortBy={sortBy} onSortChange={onSortChange} />
                  <SortableTableHeader label="Ship To" column="buildLocationName" sortBy={sortBy} onSortChange={onSortChange} />
                </>
              )}
              <SortableTableHeader label="In Hand" column="needInHandDate" sortBy={sortBy} onSortChange={onSortChange} align="center" />
              <SortableTableHeader label="Ship Date" column="shipDate" sortBy={sortBy} onSortChange={onSortChange} align="center" />
              <SortableTableHeader label="Requested By" column="createAppUserName" sortBy={sortBy} onSortChange={onSortChange} />
            </tr>
          </thead>
          <tbody>
            {statusMessage && (
              <tr className={styles.statusRow}>
                <td colSpan={columnCount} className={styles.statusCell}>
                  <span role="status">{statusMessage}</span>
                </td>
              </tr>
            )}
            {orders.map((order) => {
              const hasActiveSummary = activeSummary?.order === order;
              const isHighlighted = highlightedIds.includes(order.orderMstrId); // New orders arrived from polling get a highlight style

              return (
                <tr key={order.orderMstrId} className={hasActiveSummary ? styles.activeSummary : isHighlighted ? highlightClassName : undefined}>
                  {presentation.showPlayer && <td>{displayText(`${order.firstName?.trim() ?? ''} ${order.lastName?.trim() ?? ''}`)}</td>}
                  {presentation.showClubs && <td>{displayText(order.clubDescriptions)}</td>}
                  <td>
                    <div className={styles.orderCell}>
                      <FormNavigationLink
                        page={`Order?viewordersummary=${order.orderMstrId}&pagetype=${presentation.detailPageType}`}
                        label={order.orderNo}
                        token={token}
                        target="_blank"
                        buttonClassName={styles.orderLink}
                      />
                      <div className={styles.orderNumberDetails}>
                        <button type="button" className={styles.rowAction} aria-label="Copy order number">
                          <CopyIcon />
                        </button>
                        <button
                          type="button"
                          className={styles.rowAction}
                          aria-label="View order information"
                          onClick={(event) => setActiveSummary(activeSummary?.order === order ? null : { order, anchor: event.currentTarget })}
                        >
                          <InformationIcon className={styles.informationIcon} />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td>{formatDate(order.orderDate || order.createDate)}</td>
                  <td>{displayText(formatOrderStatus(order.orderStatusName))}</td>
                  {/* The column name is 'Tour' but the prop is 'orderAccountName' not 'tourName' */}
                  <td>{displayText(order.orderAccountName)}</td>
                  {presentation.showBuildLocation && <td>{displayText(order.buildLocationName)}</td>}
                  {presentation.showShippingLocations && (
                    <>
                      <td>{displayText(order.inventoryLocationName)}</td>
                      <td>{displayText(order.buildLocationName)}</td>
                    </>
                  )}
                  <td>{formatDate(order.needInHandDate)}</td>
                  <td>{formatDate(order.shipDate)}</td>
                  <td>{displayText(order.createAppUserName)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {summaryIsVisible && <OrderInfoTooltip order={activeSummary.order} anchor={activeSummary.anchor} onClose={() => setActiveSummary(null)} />}
    </div>
  );
}
